/**
 * @link https://formkit.com/guides/build-a-multi-step-form#putting-it-all-together
 */
import { FormKitNode } from '@formkit/core'
import { reactive, toRef, ref, watch, computed } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { NazvyKrokov, AssemblyStepConfig } from '../utils/zoppm/types'

type StepType = Pick<AssemblyStepConfig, 'jePovinny' | 'customActions'> & {
  valid: any
}

export const useSteps = createSharedComposable(() => {
  /** Názov aktuálneho kroku - string z `NazvyKrokov`. */
  const activeStep = ref<NazvyKrokov | string>('')
  /** Číslo kroku, indexované od 1, zhoduje sa s krokom v query params. */
  const activeStepNumber = ref<number>(1)
  /** Objekt kde kľúče sú názvy krokov a hodnoty údaje kroku - `Record<NazvyKrokov, StepType>` */
  const steps = reactive<Record<string, StepType>>({})
  const totalSteps = ref(0)
  const isLastStep = computed(() => {
    const lastStep = Object.keys(steps).pop()
    return lastStep === activeStep.value
  })
  const isFirstStep = computed(() => {
    const firstStep = Object.keys(steps)[0]
    return firstStep === activeStep.value
  })
  const queries = ref({})
  const route = useRoute()
  const router = useRouter()
  const nextStep = computed(() => {
    return Object.keys(steps)[activeStepNumber.value]
  })

  watch(activeStep, () => {
    const newActiveStepNumber = Object.keys(steps).indexOf(activeStep.value) + 1

    // Ak používateľ zadal krok mimo rozsah, prejdi na 1. krok.
    if (newActiveStepNumber < 1 || newActiveStepNumber > totalSteps.value + 1) {
      const stepNames = Object.keys(steps)
      activeStepNumber.value = 1
      activeStep.value = stepNames[0]
      console.warn('[zoppm] Bol zadany krok mimo rozsah formulara.')
    } else {
      activeStepNumber.value = newActiveStepNumber
    }
    router.push({
      path: route.fullPath,
      query: { ...route.query, krok: `${activeStepNumber.value}`, ...queries.value },
    })
  })

  watch(steps, () => {
    totalSteps.value = Object.keys(steps).length
  })

  const setPrevNextStep = (
    delta: number,
    urlQueries?: { [x: string]: string | number }
  ) => {
    if (urlQueries) {
      queries.value = urlQueries
    }
    const stepNames = Object.keys(steps)
    const currentIndex = stepNames.indexOf(activeStep.value)
    activeStep.value = stepNames[currentIndex + delta]
    activeStepNumber.value = Object.keys(steps).indexOf(activeStep.value) + 1
  }

  /**
   * @param stepName používa `navigacnyPopisok` zo `schemeFo.ts` alebo `schemePo.ts`
   * @param urlQueries Zvyšné query parametre.
   */
  const setStep = (
    stepName: NazvyKrokov,
    urlQueries?: { [x: string]: string | number }
  ) => {
    activeStep.value = stepName

    if (urlQueries) {
      queries.value = urlQueries
    }
  }

  const stepPlugin = (node: FormKitNode) => {
    if (node.props.type == 'group') {
      // builds an object of the top-level groups
      steps[node.name] = steps[node.name] || {}

      node.on('created', () => {
        // use 'on created' to ensure context object is available
        steps[node.name].valid = toRef(node.context!.state, 'valid')
      })
      // set the active tab to the 1st tab
      if (activeStep.value === '') {
        activeStep.value = node.name
      }
      steps[node.name].customActions = node.props?.attrs?.customActions || false
      steps[node.name].jePovinny = node.props?.attrs?.jePovinny

      // Stop plugin inheritance to descendant nodes
      return false
    }
  }

  return {
    activeStep,
    activeStepNumber,
    steps,
    stepPlugin,
    setStep,
    setPrevNextStep,
    totalSteps,
    isLastStep,
    isFirstStep,
    nextStep,
  }
})
